import { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import { LanguageContext } from './LanguageContext';
import { Link } from 'react-router-dom';

function Course() {
  const URL = process.env.REACT_APP_API_URL;
  const [courses, setCourses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const { language } = useContext(LanguageContext);

  const textContent = {
    th: {
      courseTitle: "หลักสูตรฝึกอบรม",
      loading: "กำลังโหลด...",
      objective: "วัตถุประสงค์",
      price: "ราคา",
      priceUnit: "บาท",
      seeMore: "ดูเพิ่มเติม",
      enroll: "ลงทะเบียน",
      full: "ลงทะเบียนเต็ม",
      registerDate: "วันที่ลงทะเบียน",
      trainingDate: "วันที่อบรม",      
      place: "สถานที่",
      previous: "ก่อนหน้า",
      next: "ถัดไป",
      page: "หน้า",
      of: "จาก",
    },
    en: {
      courseTitle: "Training Courses",
      loading: "Loading...",
      objective: "Objective",
      price: "Price",
      priceUnit: "THB",
      seeMore: "See More",
      enroll: "Enroll",
      full: "Full Enrollment",
      registerDate: "Register Date",
      trainingDate: "Training Date",  
      place: "Place",
      previous: "Previous",
      next: "Next",
      page: "Page",
      of: "of",
    },
  };

  const localizedText = textContent[language];

  // Fetch courses from the API
  const fetchCourses = useCallback(async (page = 1) => {
    setLoading(true);
    try {
        const response = await axios.get(`${URL}/courses?page=${page}&limit=6&enableRegister=1`);
        const allCourses = response.data.data;        

        setCourses(allCourses);        
        setTotalPages(Math.ceil(response.data.total / response.data.limit));
        setCurrentPage(response.data.page);
    } catch (error) {        
    } finally {
        setLoading(false);
    }
  }, [URL]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchCourses(page);
    }
  };

  // Format date to YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };  
  
  return (
    <div className="bg-gray-100 min-h-screen">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-center">{localizedText.courseTitle}</h1>

        {loading ? (
          <p className="text-center">{localizedText.loading}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
                <div key={course.courseId} className="bg-white p-4 rounded-lg shadow-md">
                    <img
                        src={URL + `/images/course/${course.imageFile}`}
                        alt={course.courseTopicTh}
                        className="w-full h-40 object-cover rounded-md mb-4"
                    />
                    <h3 className="text-xl font-bold mb-2">{course.courseTopicTh} ({course.courseTopicEn})</h3>
                    <p className="text-gray-700 mb-2">
                        <strong>{localizedText.objective}:</strong> {course.objective} 
                    </p>

                    <p className="text-gray-700 mb-2">                       
                        <strong>{localizedText.trainingDate}:</strong> {formatDate(course.traningDate)} ({course.startTime.substring(0, 5)} - {course.endTime.substring(0, 5)})
                    </p>
                    
                    <p className="text-gray-700 mb-2">                       
                        <strong>{localizedText.price}:</strong> {Number(course.price).toLocaleString()} {localizedText.priceUnit} 
                    </p>

                    <div className="mt-4 text-sm">
                        <Link
                            to={`/course/${course.courseId}`}
                            className="text-blue-600 hover:underline hover:text-blue-800"
                        >
                            {localizedText.seeMore}
                        </Link>
                        <span className="mx-2 text-gray-400">|</span>

                        {course.customerCount < course.maxSeat ? (
                          <Link to={`/course/${course.courseId}#registers`} className="text-green-600 hover:underline hover:text-green-800">
                            {localizedText.enroll}
                          </Link>
                        ) : (
                          <span className="text-red-600">{localizedText.full}</span>
                        )} 
                 
                    </div>
                
                </div>
            ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-blue-900 text-white rounded-lg mr-2 disabled:opacity-50"
                >
                  {localizedText.previous}
                </button>
                <span className="px-4 py-2 bg-gray-200 rounded-lg">
                  {localizedText.page} {currentPage} {localizedText.of} {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-blue-900 text-white rounded-lg ml-2 disabled:opacity-50"
                >
                  {localizedText.next}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default Course;